import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import { TelephoneIcon } from '@repo/ui/icons'
import Modal, { MODAL_CONTENT_CLASSNAME } from '@repo/ui/modal'
import SelectInput from '@repo/ui/select-input'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { assignMobileNumberToCenter } from '../../../api/centers/assign-mobile-number-to-center'
import { getCenter } from '../../../api/centers/get-center'
import { getMobileNumberForFrenchLeaders } from '../../../api/ols/mobile-number-for-french-leaders/get-mobile-number-for-french-leaders'
import LoadingScreen from '../../../components/elements/loading/loading-screen'
import { getOlPermissions } from '../../../lib/constants/ol-permissions'
import { Paths } from '../../../lib/constants/path'
import useAuth from '../../../lib/hooks/use-auth'
import { useDataProps } from '../../../lib/hooks/use-data-props'
import useTranslate from '../../../lib/hooks/use-translate'
import { Center } from '../../../lib/types/models/center/center'
import { CenterProperties } from '../../../lib/types/models/center/properties.center'
import { DataProps } from '../../../lib/types/utils'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { formatPhoneNumber } from '../../../utils/functions/format-phone-number'
import { constructOlPersonalSpaceCenterPath } from '../../ol-personal-space/centers/utils/functions'

type Props = {
  isOpen: boolean
  onCancel: () => void
}

export function ChangeFrenchLeaderPhoneNumberModel(props: DataProps<Center, Props>) {
  const { id, data: center, isOpen, onCancel } = useDataProps(props, (id) => getCenter(id))
  const { t } = useTranslate()
  const { user } = useAuth()
  const olId = user?.ol?.id
  const { navigate } = useNavigator()
  const queryClient = useQueryClient()

  const olPermissions = getOlPermissions({ role: user?.role, type: center?.ol?.type })

  const { data: mobileNumberForFrenchLeaders, isFetching } = useQuery({
    queryKey: ['mobile-number-for-french-leaders'],
    queryFn: () => {
      if (!olId) return
      return getMobileNumberForFrenchLeaders({ olId })
    },
    enabled: !!olId && olPermissions['edit-mobile-number-for-french-leaders']
  })

  const assignNumberForFrenchLeaders = useMutation({
    mutationFn: assignMobileNumberToCenter,
    mutationKey: ['assign-mobile-number-for-french-leaders'],
    onError: showAxiosError,
    onSuccess: (_, assignMobileToCenterRequest) => {
      toast.success('Mobile number assigned successfully!')
      queryClient.invalidateQueries({ queryKey: ['centers', id] })

      // Update the center with the new mobile number instead of refetching the center
      const newMobileNumber = mobileNumberForFrenchLeaders?.data.find(
        (number) => number.id === assignMobileToCenterRequest.mobileNumberForFrenchLeaderId
      )
      if (center && newMobileNumber) center.mobileNumberForFrenchLeader = newMobileNumber
    }
  })

  const selectedMobileNumber = mobileNumberForFrenchLeaders?.data.find(
    (number) => number.id === center?.mobileNumberForFrenchLeader?.id
  )

  if (!center || isFetching) return <LoadingScreen />

  return (
    <Modal
      icon={TelephoneIcon}
      isOpen={isOpen}
      title={t('ol.center.placement.change.french.leader.phone.number')}
      onCancel={onCancel}
      hasBorderBottom
      actionButtonsOptions={{ cancelText: t('button.close') }}
    >
      <div className={cn(MODAL_CONTENT_CLASSNAME, 'flex flex-col gap-3')}>
        <div className="flex flex-row items-center gap-2">
          <Badge.Link
            text={center.name.toLocaleUpperCase()}
            color={tailwindColors.white.active}
            textColor={tailwindColors.primary.DEFAULT}
            onClick={() =>
              navigate(constructOlPersonalSpaceCenterPath(Paths.OL_CENTER_PLACEMENTS, center.id))
            }
          />

          <Badge text={CenterProperties.getCenterDates(center)} />
        </div>

        <SelectInput
          label={t('ol.center.placement.diffusion.phone.number')}
          options={
            mobileNumberForFrenchLeaders?.data.map((number) => ({
              value: number.id,
              label: formatPhoneNumber(number.phoneNumber)
            })) || []
          }
          selectedOption={
            selectedMobileNumber
              ? {
                  label: formatPhoneNumber(selectedMobileNumber.phoneNumber),
                  value: selectedMobileNumber.id
                }
              : undefined
          }
          onChange={(value) => {
            if (!value) return
            assignNumberForFrenchLeaders.mutate({
              centerId: id,
              mobileNumberForFrenchLeaderId: value
            })
          }}
        />
      </div>
    </Modal>
  )
}
